import { useMemo } from 'react';
import type { StudyDetailResponse } from '@/api/generated/types.gen';
import { useStore } from '@/stores/useStore';
import type { GeometryMeshingStatus } from './geometryMeshing';

/**
 * The geometries a draft meshes: its live inputs while the store holds this
 * study — a geometry just uploaded is there before autosave writes it back —
 * else the saved list (ENG-3100). A mesh-in draft has none.
 */
export function useDraftGeometryIds(
  studyId: string,
  study: Pick<StudyDetailResponse, 'config'> | undefined,
): string[] {
  const draftStudyId = useStore((s) => s.draftStudyId);
  const draftInputs = useStore((s) => s.draftConfig.selected_inputs);
  const savedIds = study?.config?.inputs?.geometry_ids;
  return useMemo(() => {
    if (draftStudyId === studyId && draftInputs) {
      return draftInputs.kind === 'geometry'
        ? draftInputs.items.map((g) => g.id)
        : [];
    }
    return savedIds ?? [];
  }, [draftStudyId, studyId, draftInputs, savedIds]);
}

/** Whether every one of `ids` has a completed mesh. */
export function allMeshed(
  ids: string[],
  statusByGeomId: Map<string, GeometryMeshingStatus>,
): boolean {
  return (
    ids.length > 0 && ids.every((id) => statusByGeomId.get(id) === 'completed')
  );
}
